/**
 * IndexedDB-backed persistence for resumable chunked uploads.
 *
 * After every successfully uploaded chunk, the uploader calls `saveResumeState`
 * with the current filekey + offset. If the upload completes, the entry is
 * removed via `clearResumeState`. If the upload fails (network drop, server
 * error, page reload), the entry survives - and the app can offer to resume
 * from that offset using the SAME file the user picks again.
 *
 * Stale entries (>6 hours old) are pruned automatically; MediaWiki expires
 * stash filekeys around the same horizon, so older entries are unusable.
 */

import { openDB } from "idb"
import type { IDBPDatabase, DBSchema } from "idb"
import type { ResumeState, LocalizedDescription, UploadForm } from "./types"

const DB_NAME = "c2c-storage"
const DB_VERSION = 1
const STORE_NAME = "resumable-uploads"
const MAX_AGE_MS = 6 * 60 * 60 * 1000

interface ResumeDBSchema extends DBSchema {
  "resumable-uploads": {
    key: string
    value: ResumeState
  }
}

let dbPromise: Promise<IDBPDatabase<ResumeDBSchema>> | null = null

function getDb(): Promise<IDBPDatabase<ResumeDBSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<ResumeDBSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "id" })
        }
      }
    })
  }
  return dbPromise
}

function isExpired(state: ResumeState): boolean {
  const t = Date.parse(state.createdAt)
  if (!Number.isFinite(t)) return true
  return Date.now() - t > MAX_AGE_MS
}

/** Save (insert-or-replace) a resume entry. Errors are swallowed - losing
 *  resume state is annoying but never user-facing-broken. */
export async function saveResumeState(state: ResumeState): Promise<void> {
  try {
    const db = await getDb()
    await db.put(STORE_NAME, state)
  } catch {
    /* IndexedDB unavailable (private mode, quota, etc.) - silently ignore */
  }
}

/** Look up a single resume entry by id (filename). Returns null if missing
 *  or expired. */
export async function getResumeState(id: string): Promise<ResumeState | null> {
  try {
    const db = await getDb()
    const state = await db.get(STORE_NAME, id)
    if (!state) return null
    if (isExpired(state)) {
      await db.delete(STORE_NAME, id)
      return null
    }
    return state
  } catch {
    return null
  }
}

/** List all non-expired resume entries. */
export async function listResumeStates(): Promise<ResumeState[]> {
  try {
    const db = await getDb()
    const all = await db.getAll(STORE_NAME)
    const fresh: ResumeState[] = []
    for (const s of all) {
      if (isExpired(s)) {
        await db.delete(STORE_NAME, s.id)
      } else {
        fresh.push(s)
      }
    }
    fresh.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    return fresh
  } catch {
    return []
  }
}

/** Drop a single entry. */
export async function clearResumeState(id: string): Promise<void> {
  try {
    const db = await getDb()
    await db.delete(STORE_NAME, id)
  } catch {
    /* ignore */
  }
}

/** Drop everything. Useful for "Start fresh" in the UI. */
export async function clearAllResumeStates(): Promise<void> {
  try {
    const db = await getDb()
    await db.clear(STORE_NAME)
  } catch {
    /* ignore */
  }
}

/**
 * The fields a resumed upload restores onto its rebuilt form. The stored
 * wikitext already carries the description, licence, categories and date;
 * captions and the Wikidata item are written after the upload from the form,
 * so they have to come back from the entry. Entries saved before these were
 * stored lack them and fall back to the fresh form's defaults. Copies, so
 * editing the form can never reach back into a stored entry.
 */
export function restoredPostUploadFields(
  state: ResumeState,
  fallbackCaptions: LocalizedDescription[]
): Pick<UploadForm, "captions" | "wikidataItem"> {
  return {
    captions: state.captions?.length ? state.captions.map(c => ({ ...c })) : fallbackCaptions,
    wikidataItem: state.wikidataItem ? { ...state.wikidataItem } : null
  }
}
